import React, { useEffect, useState } from 'react';
import { PageHeaderLeftIcon } from '@shared/infrastructure/ui/components/header/pageHeader';
import { historyRepository } from '@shared/domain/router/router.repository';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { subtopicRepository } from '@shared/domain/subtopic/subtopic.repository';
import { ROUTES } from '@shared/infrastructure/router/paths';
import { useAppSelector } from '@shared/infrastructure/state/store';
import {
  countryCodeSelector,
  languageCodeSelector
} from '@shared/infrastructure/state/global.slice';
import { Topic } from '@shared/domain/topic/topic.aggregate';
import { Subtopic } from '@shared/domain/subtopic/subtopic.aggregate';
import { Content } from '@shared/domain/content/content.aggregate';
import { SubtopicEditLayout } from './subtopicEdit/subtopicEditLayout';
import { SubtopicInfo } from './subtopicEdit/subtopicInfo';
import { SubtopicSurvey } from './subtopicEdit/subtopicSurvey';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Box, Button, IconButton } from '@mui/material';
import { contentRepository } from '@shared/domain/content/content.repository';
import { SubtopicContent } from './subtopicEdit/subtopicContent';
import { topicRepository } from '@shared/domain/topic/topic.repository';
import { Therapy } from '@shared/domain/therapy/therapy.aggregate';
import { therapyRepository } from '@shared/domain/therapy/therapy.repository';
import { routeRepository } from '@shared/domain/router/route/route.repository';

export const SubtopicCreate = () => {
  const countryCode = useAppSelector(countryCodeSelector);
  const languageCode = useAppSelector(languageCodeSelector);
  const { therapyId, topicId } =
    routeRepository.getParams(
      ROUTES.SUBTOPICS_CREATE.path,
      window.location.pathname
    ) || {};

  const [therapy, setTherapy] = useState<Therapy>();
  useEffect(() => {
    therapyRepository
      .getTherapies(countryCode, languageCode)
      .then((therapies) => {
        setTherapy(therapies.find((th) => th.id === Number(therapyId)));
      });
  }, [countryCode, languageCode, therapyId]);

  const [topic, setTopic] = useState<Topic>();
  useEffect(() => {
    topicRepository.getTopics(countryCode, languageCode).then((topics) => {
      setTopic(topics.find((t) => t.id === Number(topicId)));
    });
  }, [countryCode, languageCode, topicId]);

  const [contents, setContents] = useState<Content[]>([]);
  useEffect(() => {
    contentRepository
      .getContents(countryCode, languageCode)
      .then((contents) => {
        setContents(contents || []);
      });
  }, [countryCode, languageCode]);

  const { control, handleSubmit, setError, watch } = useForm<Subtopic>({
    defaultValues: {
      name: '',
      nameLanguage: '',
      descriptionLanguage: '',
      contents: [],
      survey: {
        description: '',
        options: { Great: '', Promising: '', NoValue: '', BadIdea: '' }
      }
    }
  });
  const back = () =>
    historyRepository.push(
      routeRepository.buildRoute(ROUTES.SUBTOPICS_FILTERED.path, {
        therapyId: String(therapyId),
        topicId: String(topicId)
      })
    );
  const onSubmit: SubmitHandler<Subtopic> = (data) => {
    subtopicRepository
      .createSubtopic(countryCode, languageCode, {
        ...data,
        topicId: Number(topicId)
      })
      .then(back, (error) => {
        if (error.cause?.response?.status === 409) {
          setError('name', {
            message: 'This identifier is already taken for the selected topic.'
          });
        }
        throw error;
      });
  };
  const name = watch('name');

  return (
    <>
      <PageHeaderLeftIcon
        icon={
          <IconButton
            size='large'
            color='inherit'
            aria-label='back button'
            sx={{ mr: 2 }}
            onClick={back}
          >
            <ArrowBackIcon fontSize='large' />
          </IconButton>
        }
      >
        <Box sx={{ textAlign: 'center' }}>
          <p>Create subtopic</p>
          <p>
            <small>
              Country: {countryCode}, Language: {languageCode}
            </small>
          </p>
        </Box>
      </PageHeaderLeftIcon>
      <Box sx={{ textAlign: 'center' }}>
        <p>
          {`/${countryCode}/${therapy?.name}/room/${topic?.name}/${
            name || 'identifier'
          }/modal`}
        </p>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <SubtopicEditLayout
          left={<SubtopicInfo topic={topic} control={control} />}
          middle={<SubtopicContent contents={contents} control={control} />}
          right={<SubtopicSurvey control={control} />}
          buttons={
            <Button
              sx={{
                marginTop: '1rem',
                padding: '1rem 1.5rem'
              }}
              type='submit'
              variant='contained'
              color='primary'
            >
              Create
            </Button>
          }
        />
      </form>
    </>
  );
};
